
import React, { useContext, useEffect, useState } from "react";
import {
  TablePagination,
  Paper,
  Button,
  Dialog,
  Select,
  MenuItem,
  Breadcrumbs,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  IconButton,
} from "@mui/material";

import {
  MdClose,
  MdOutlineEmail,
  MdOutlineCurrencyRupee,
  MdPlace,
  MdInfo,
  MdCancel,
  MdWarning
} from "react-icons/md";
import { FaPhoneAlt } from "react-icons/fa";
import { emphasize, styled } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import { fetchDataFromApi, editData } from "../../utils/api";
import { MyContext } from "../../App";
import { TextField } from "@mui/material";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const getProductNameWithVariant = (item) => {
  if (!item) return "N/A";

  const baseProductName = item?.productTitle?.substr(0, 50) || "";

  if (item?.selectedVariantOption && item?.selectedVariantOption !== "No variant") {
    return ` ${item.selectedVariantOption}`;
  }

  return baseProductName + (baseProductName.length > 50 ? "..." : "");
};

const columns = [
  { id: "orderId", label: "Order Id", minWidth: 150 },
  { id: "paymentId", label: "Payment Id", minWidth: 100 },
  { id: "products", label: "Products", minWidth: 150 },
  { id: "name", label: "Name", minWidth: 130 },
  { id: "phoneNumber", label: "Phone Number", minWidth: 150 },
  { id: "address", label: "Address", minWidth: 200 },
  { id: "pincode", label: "Pincode", minWidth: 120 },
  { id: "totalAmount", label: "Total Amount", minWidth: 120 },
  { id: "email", label: "Email", minWidth: 120 },
  { id: "userId", label: "User Id", minWidth: 120 },
  { id: "orderStatus", label: "Order Status", minWidth: 120 },
  { id: "dateCreated", label: "Date Created", minWidth: 150 },
  { id: "actions", label: "Actions", minWidth: 100 },
];

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
  const [isOpenRejectionModal, setIsOpenRejectionModal] = useState(false);  // ✅ NEW
  const [selectedCancelledOrder, setSelectedCancelledOrder] = useState(null);
  const [selectedRejectedOrder, setSelectedRejectedOrder] = useState(null);  // ✅ NEW
  const [statusVal, setStatusVal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page1, setPage1] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const context = useContext(MyContext);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortOrdersByDate = (orders, orderType) => {
    return [...orders].sort((a, b) => {
      const dateA = new Date(a.date || a.dateCreated);
      const dateB = new Date(b.date || b.dateCreated);
      return orderType === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  const loadOrders = () => {
    fetchDataFromApi(`/api/orders`)
      .then((response) => {
        if (response && Array.isArray(response)) {
          setOrders(response);
        } else {
          console.error("Invalid orders data received:", response);
          setOrders([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching orders:", error);
        setOrders([]);
      });
  };

  const showProducts = (id) => {
    if (!id) return;

    fetchDataFromApi(`/api/orders/${id}`)
      .then((res) => {
        if (res && res.products) {
          setIsOpenModal(true);
          setProducts(res.products);
        } else {
          console.error("No products found for order:", id);
          setProducts([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching order products:", error);
        setProducts([]);
      });
  };

  // ✅ Show cancellation details (old modal for cancelled status)
  const showCancellationDetails = (order) => {
    if (!order) {
      console.error("Invalid cancelled order data");
      return;
    }
    setSelectedCancelledOrder(order);
    setIsOpenCancelModal(true);
  };

  // ✅ NEW: Show rejection details (delivery boy rejection)
  const showRejectionDetails = (order) => {
    if (!order || !order.rejectionHistory || order.rejectionHistory.length === 0) {
      console.error("Invalid rejection data");
      return;
    }
    setSelectedRejectedOrder(order);
    setIsOpenRejectionModal(true);
  };

  const handleChangeStatus = (e, orderId) => {
    if (!orderId) return;

    const newStatus = e.target.value;
    setStatusVal(newStatus);
    setIsLoading(true);
    if (context?.setProgress) context.setProgress(40);

    fetchDataFromApi(`/api/orders/${orderId}`)
      .then((res) => {
        if (!res) {
          throw new Error("Order not found");
        }

        const order = {
          name: res.name,
          phoneNumber: res.phoneNumber,
          address: res.address,
          pincode: res.pincode,
          amount: parseInt(res.amount),
          paymentId: res.paymentId,
          email: res.email,
          userid: res.userid || res.userId,
          products: res.products,
          status: newStatus,
        };

        editData(`/api/orders/${orderId}`, order)
          .then(() => {
            loadOrders();
            if (context?.setProgress) context.setProgress(100);
            setIsLoading(false);
            setStatusVal(null);
          })
          .catch((error) => {
            console.error("Error updating order status:", error);
            setIsLoading(false);
            if (context?.setProgress) context.setProgress(100);
          });
      })
      .catch((error) => {
        console.error("Error fetching order for update:", error);
        setIsLoading(false);
        if (context?.setProgress) context.setProgress(100);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage1(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage1(0);
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return "";
    try {
      const date = new Date(datetime);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "#ffa726";
      case "confirmed":
        return "#29b6f6";
      case "processing":
        return "#ab47bc";
      case "shipped":
        return "#5c6bc0";
      case "delivered":
        return "#66bb6a";
      case "cancelled":
        return "#ef5350";
      case "rejected":  // ✅ NEW
        return "#ff9800";
      default:
        return "#9e9e9e";
    }
  };

  const getStatusBadgeStyle = (status) => ({
    backgroundColor: getStatusColor(status),
    color: "white",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "bold",
    textTransform: "uppercase",
  });

  return (
    <div className="right-content w-100">
      {/* Header */}
      <div className="card shadow border-0 w-100 flex-row p-4 align-items-center">
        <h5 className="mb-0">Orders List</h5>
        <div className="ml-auto d-flex align-items-center" style={{ gap: "12px" }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Order ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mr: 2, minWidth: 200 }}
          />

          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            size="small"
            sx={{ mr: 2, minWidth: 140 }}
          >
            <MenuItem value="desc">Newest First</MenuItem>
            <MenuItem value="asc">Oldest First</MenuItem>
          </Select>
          <Breadcrumbs aria-label="breadcrumb" className="ml-auto breadcrumbs_">
            <StyledBreadcrumb
              component="a"
              href="#"
              label="Dashboard"
              icon={<HomeIcon fontSize="small" />}
            />
            <StyledBreadcrumb label="Orders" deleteIcon={<ExpandMoreIcon />} />
          </Breadcrumbs>
        </div>
      </div>

      {/* MAIN: card-list */}
      <div className="card shadow border-0 p-3 mt-4">
        <Paper sx={{ width: "100%", overflow: "hidden", p: 2 }}>
          {orders && orders.length > 0 ? (
            sortOrdersByDate(
              orders.filter(order =>
                order?._id?.toLowerCase().includes(searchTerm.toLowerCase())
              ),
              sortOrder
            )
              .slice(page1 * rowsPerPage, page1 * rowsPerPage + rowsPerPage)
              .map((order, index) => {
                if (!order) return null;

                const serialNumber = page1 * rowsPerPage + index + 1;
                const lat = order.latitude;
                const lng = order.longitude;
                const encodedAddress = encodeURIComponent(order?.address || "");

                return (
                  <Paper
                    elevation={2}
                    key={order._id || index}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      backgroundColor: 
                        order.status === "cancelled" ? "#fff8f8" : 
                        order.status === "rejected" ? "#fff3e0" :  // ✅ NEW
                        "white",
                      borderLeft: `6px solid ${getStatusColor(order.status)}`,
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    {/* Serial number column */}
                    <Box
                      sx={{
                        width: 50,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        pt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          backgroundColor: "#346e60ff",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          fontSize: "14px",
                          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.38)",
                        }}
                      >
                        {serialNumber}
                      </Box>
                    </Box>

                    {/* Main order content */}
                    <Box sx={{ flex: 1 }}>
                      {/* Top row: Order id, payment type, quick actions */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            #Order_ID-{order?._id || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(order.date || order.dateCreated)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Typography variant="body2">
                            {(order.paymentMethod === "cod" ||
                              order.paymentMethod === "Cash on Delivery") ||
                              !order.paymentId
                              ? "Cash on Delivery"
                              : order.paymentId || "N/A"}
                          </Typography>

                          <span style={getStatusBadgeStyle(order?.status)}>
                            {order?.status || "N/A"}
                          </span>
                        </Box>
                      </Box>

                      {/* Expandable details */}
                      <Accordion sx={{ mt: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2" fontWeight="bold">
                            View Details
                          </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                          {/* Grid-like two-column layout */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                              gap: 2,
                            }}
                          >
                            {/* Left column: customer, contact, products quick list */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Customer
                              </Typography>
                              <Typography variant="body2">
                                <strong>Name:</strong> {order?.name || "N/A"}
                              </Typography>
                              <Typography variant="body2">
                                <FaPhoneAlt style={{ marginRight: 6 }} />
                                {order?.phoneNumber || "N/A"}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <MdOutlineEmail style={{ marginRight: 6 }} />
                                {order?.email || "N/A"}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>UserId:</strong>{" "}
                                {order?.userid || order?.userId || "N/A"}
                              </Typography>

                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">
                                  Products (preview)
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                                  {order.products && order.products.length > 0 ? (
                                    order.products.slice(0, 4).map((item, i) => (
                                      <li key={i} style={{ marginBottom: 6 }}>
                                        {getProductNameWithVariant(item)} — Qty:{" "}
                                        {item.quantity || 0}
                                      </li>
                                    ))
                                  ) : (
                                    <li>No products found</li>
                                  )}
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => showProducts(order?._id)}
                                >
                                  View
                                </Button>
                              </Box>
                            </Box>

                            {/* Right column: address, map, amount, pincode */}
                            <Box>
                              <Typography variant="subtitle2">Address</Typography>
                              <Typography variant="body2">
                                {order?.address || "N/A"}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Pincode:</strong> {order?.pincode || "N/A"}
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                  mt: 1,
                                }}
                              >
                                <MdPlace style={{ color: "red" }} />
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    window.open(
                                      lat && lng
                                        ? `https://www.google.com/maps?q=${lat},${lng}`
                                        : `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
                                      "popup",
                                      "width=800,height=600"
                                    )
                                  }
                                >
                                  View in Maps
                                </Button>
                              </Box>

                              <Box sx={{ mt: 1 }}>
                                <iframe
                                  width="100%"
                                  height="200"
                                  style={{ border: 0, borderRadius: 8 }}
                                  loading="lazy"
                                  allowFullScreen
                                  referrerPolicy="no-referrer-when-downgrade"
                                  src={
                                    lat && lng
                                      ? `https://www.google.com/maps/embed/v1/view?key=AIzaSyAaCKtKlpRBMh41ZrmZ5q1G6-MSm9ymlCY&q=${lat},${lng}&zoom=18&maptype=roadmap`
                                      : `https://www.google.com/maps/embed/v1/place?key=AIzaSyAaCKtKlpRBMh41ZrmZ5q1G6-MSm9ymlCY&q=${encodedAddress}`
                                  }
                                  title={`Map-${order._id || index}`}
                                ></iframe>
                              </Box>

                              <Typography sx={{ mt: 1 }}>
                                <MdOutlineCurrencyRupee style={{ marginRight: 6 }} />
                                {order?.amount || 0}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Middle area: payment, status select, cancellation/rejection summary */}
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              gap: 2,
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2">
                              <strong>Payment:</strong>{" "}
                              {(order.paymentMethod === "cod" ||
                                order.paymentMethod === "Cash on Delivery") ||
                                !order.paymentId
                                ? "Cash on Delivery"
                                : order.paymentId || "N/A"}
                            </Typography>

                            <Box>
                              <Select
                                disabled={isLoading}
                                value={order?.status || ""}
                                onChange={(e) => handleChangeStatus(e, order?._id)}
                                displayEmpty
                                size="small"
                                sx={{
                                  minWidth: 140,
                                  backgroundColor: "white",
                                  borderRadius: "4px",
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select Status</em>
                                </MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="confirmed">Confirmed</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="shipped">Shipped</MenuItem>
                                <MenuItem value="delivered">Delivered</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                              </Select>
                            </Box>

                            <Box>
                              <span style={getStatusBadgeStyle(order?.status)}>
                                {order?.status || "N/A"}
                              </span>

                              {/* ✅ SHOW CANCELLATION DETAILS */}
                              {order.status === "cancelled" && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    Cancelled by: {order.cancelledBy || "N/A"}
                                  </Typography>
                                  <br />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTime(order.cancelledAt)}
                                  </Typography>
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      startIcon={<MdInfo />}
                                      onClick={() => showCancellationDetails(order)}
                                    >
                                      View Reason
                                    </Button>
                                  </Box>
                                </Box>
                              )}

                              {/* ✅ NEW: SHOW REJECTION DETAILS */}
                              {order.status === "pending" && 
                               order.rejectionHistory && 
                               order.rejectionHistory.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Alert severity="warning" sx={{ py: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 0.5 }}
                                    >
                                      <MdWarning size={16} />
                                      Rejected by delivery boy
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                      {order.rejectionHistory[order.rejectionHistory.length - 1]?.rejectedBy || "Unknown"}
                                    </Typography>
                                  </Alert>
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      variant="outlined"
                                      color="warning"
                                      size="small"
                                      startIcon={<MdInfo />}
                                      onClick={() => showRejectionDetails(order)}
                                    >
                                      View Rejections ({order.rejectionHistory.length})
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </Paper>
                );
              })
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 6 }}>
              No orders found
            </Typography>
          )}

          {/* Pagination */}
          <Box sx={{ mt: 2 }}>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={
                orders.filter(order =>
                  order?._id?.toLowerCase().includes(searchTerm.toLowerCase())
                ).length
              }
              rowsPerPage={rowsPerPage}
              page={page1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                "& .MuiTablePagination-select": {
                  paddingRight: "35px !important",
                },
                "& .MuiSelect-icon": {
                  position: "absolute",
                  right: "12px",
                  pointerEvents: "none",
                },
                "& .MuiInputBase-root": {
                  position: "relative",
                },
              }}
            />
          </Box>
        </Paper>
      </div>

      {/* Products Modal */}
      <Dialog open={isOpenModal} className="productModal" maxWidth="lg" fullWidth onClose={() => setIsOpenModal(false)}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Products</Typography>
          <IconButton onClick={() => setIsOpenModal(false)}>
            <MdClose />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <div className="table-responsive orderTable">
            <table className="table table-striped table-bordered" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#f5f5f5" }}>
                <tr>
                  <th style={{ padding: 8, textAlign: "left" }}>Product Id</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Product Title</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Image</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Quantity</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Price</th>
                  <th style={{ padding: 8, textAlign: "left" }}>SubTotal</th>
                </tr>
              </thead>
              <tbody>
                {products && products.length > 0 ? (
                  products.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: 8 }}>{item?.productId || "N/A"}</td>
                      <td style={{ padding: 8, whiteSpace: "normal", maxWidth: 300 }}>
                        {getProductNameWithVariant(item).length > 60
                          ? getProductNameWithVariant(item).substr(0, 60) + "..."
                          : getProductNameWithVariant(item)}
                      </td>
                      <td style={{ padding: 8 }}>
                        <div style={{ width: 50, height: 50, overflow: "hidden", borderRadius: 6 }}>
                          <img
                            src={item?.image || "/placeholder-image.jpg"}
                            alt="product"
                            width="50"
                            style={{ objectFit: "cover" }}
                            onError={(e) => (e.target.src = "/placeholder-image.jpg")}
                          />
                        </div>
                      </td>
                      <td style={{ padding: 8 }}>{item?.quantity || 0}</td>
                      <td style={{ padding: 8 }}>₹{item?.price || 0}</td>
                      <td style={{ padding: 8 }}>₹{item?.subTotal || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", padding: 16 }}>
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsOpenModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Cancellation Details Modal */}
      <Dialog
        open={isOpenCancelModal}
        onClose={() => setIsOpenCancelModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#ffebee", color: "#c62828" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MdCancel size={24} />
            <Typography variant="h6">Order Cancellation Details</Typography>
          </Box>
          <IconButton onClick={() => setIsOpenCancelModal(false)}>
            <MdClose />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedCancelledOrder && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Order Information */}
              <Paper elevation={2} sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Order Information
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedCancelledOrder._id || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Name:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedCancelledOrder.name || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Order Amount:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      ₹{selectedCancelledOrder.amount || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {(selectedCancelledOrder.paymentMethod === "cod" || selectedCancelledOrder.paymentMethod === "Cash on Delivery")
                        ? "Cash on Delivery"
                        : selectedCancelledOrder.paymentMethod || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Cancellation Details */}
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Cancellation Information
                </Typography>

                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Cancelled By:</strong>
                    </Typography>
                    <Chip
                      label={selectedCancelledOrder.cancelledBy === "customer" ? "Customer" : "Admin"}
                      color={selectedCancelledOrder.cancelledBy === "customer" ? "warning" : "info"}
                      size="small"
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Cancellation Date & Time:</strong>
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(selectedCancelledOrder.cancelledAt)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Reason for Cancellation:</strong>
                    </Typography>
                    <Paper elevation={1} sx={{ p: 2, backgroundColor: "#fff3e0", border: "1px solid #ffcc02", borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                        "{selectedCancelledOrder.cancellationReason || "No reason provided"}"
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Customer Contact Information */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Customer Contact:</strong>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Chip
                        icon={<MdOutlineEmail />}
                        label={selectedCancelledOrder.email || "N/A"}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        icon={<FaPhoneAlt />}
                        label={selectedCancelledOrder.phoneNumber || "N/A"}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Refund Information */}
                  {selectedCancelledOrder.paymentMethod !== "cod" &&
                    selectedCancelledOrder.paymentMethod !== "Cash on Delivery" &&
                    selectedCancelledOrder.paymentId && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Refund Information:</strong><br />
                          This order was paid online (Payment ID: {selectedCancelledOrder.paymentId}).
                          Please process the refund within 5-7 business days as per company policy.
                        </Typography>
                      </Alert>
                    )}
                </Box>
              </Alert>

              {/* Products Information */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Cancelled Products
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                  {selectedCancelledOrder.products && selectedCancelledOrder.products.length > 0 ? (
                    selectedCancelledOrder.products.map((product, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2, p: 1, borderBottom: idx < selectedCancelledOrder.products.length - 1 ? "1px solid #eee" : "none" }}>
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.productTitle || "Product"}
                            width="40"
                            height="40"
                            style={{ borderRadius: 4, objectFit: "cover" }}
                            onError={(e) => { e.target.src = "/placeholder-image.jpg"; }}
                          />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getProductNameWithVariant(product)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Qty: {product.quantity || 0} | Price: ₹{product.price || 0}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          ₹{product.subTotal || 0}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      No products found
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
          <Button onClick={() => setIsOpenCancelModal(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Rejection Details Modal - Shows rejection history */}
      <Dialog
        open={isOpenRejectionModal}
        onClose={() => setIsOpenRejectionModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fff3e0", color: "#e65100" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MdWarning size={24} />
            <Typography variant="h6">Order Rejection History</Typography>
          </Box>
          <IconButton onClick={() => setIsOpenRejectionModal(false)}>
            <MdClose />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedRejectedOrder && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Order Information */}
              <Paper elevation={2} sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Order Information
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedRejectedOrder._id || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Name:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedRejectedOrder.name || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Order Amount:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      ₹{selectedRejectedOrder.amount || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Current Status:</Typography>
                    <Chip label={selectedRejectedOrder.status} color="warning" size="small" />
                  </Box>
                </Box>
              </Paper>

              {/* Rejection Details */}
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Rejection Information
                </Typography>

                <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
                  <strong>Total Rejections: {selectedRejectedOrder.rejectionHistory?.length || 0}</strong>
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {selectedRejectedOrder.rejectionHistory && selectedRejectedOrder.rejectionHistory.length > 0 ? (
                    selectedRejectedOrder.rejectionHistory.map((rejection, idx) => (
                      <Paper key={idx} elevation={1} sx={{ p: 2, backgroundColor: "#fffde7", border: "1px solid #fbc02d", borderRadius: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <MdWarning size={20} color="#e65100" />
                          <Typography variant="subtitle2" fontWeight="bold" color="#e65100">
                            Rejection #{idx + 1}
                          </Typography>
                        </Box>

                        <Box sx={{ ml: 3 }}>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Rejected by:</strong>
                            </Typography>
                            <Chip 
                              label={rejection.rejectedBy || "Unknown"} 
                              color="warning" 
                              variant="outlined"
                              size="small"
                              icon={<FaPhoneAlt />}
                            />
                          </Box>

                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Date & Time:</strong>
                            </Typography>
                            <Typography variant="body2">
                              {formatDateTime(rejection.rejectedAt)}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Rejection Reason:</strong>
                            </Typography>
                            <Paper elevation={0} sx={{ p: 1.5, backgroundColor: "#f5f5f5", borderLeft: "3px solid #ff9800", borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                                "{rejection.rejectionReason || "No reason provided"}"
                              </Typography>
                            </Paper>
                          </Box>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No rejection history found
                    </Typography>
                  )}
                </Box>
              </Alert>

              {/* Customer Contact Information */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Contact
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
                  <Chip
                    icon={<MdOutlineEmail />}
                    label={selectedRejectedOrder.email || "N/A"}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    icon={<FaPhoneAlt />}
                    label={selectedRejectedOrder.phoneNumber || "N/A"}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Paper>

              {/* Action Info */}
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Note:</strong> This order was rejected by delivery agent(s) and is now back to "Pending" status. 
                  It is available for reassignment to another delivery agent.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
          <Button onClick={() => setIsOpenRejectionModal(false)} variant="contained" color="warning">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Orders;

